import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import clsx from "clsx";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import Avatar from "@/components/ui/Avatar";
import { Textarea, Checkbox } from "@/components/ui/Field";

export type ThreadMessage = {
  id: number;
  body: string;
  urgent: boolean;
  from_hq: boolean;
  author_name: string;
  author_id: number;
  avatar: string | null;
  created_at: string;
};

// Polled rather than pushed over a websocket. This gets used on a phone on
// venue wifi on the day, where a dropped socket is the common case and a
// refetch just works. A few seconds of delay is a fair trade.
const POLL_MS = 5000;

function timeLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    ...(today ? {} : { month: "short", day: "numeric" }),
  });
}

export default function EventThread({
  eventId,
  initialMessages,
  isHq,
}: {
  eventId: number;
  initialMessages: ThreadMessage[];
  isHq: boolean;
}) {
  const [messages, setMessages] = useState<ThreadMessage[]>(initialMessages);
  const [body, setBody] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [sending, setSending] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  // Only auto-scroll when they're already at the bottom, so a new message
  // doesn't yank the view while someone is reading back.
  function onScroll() {
    const el = scroller.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  useEffect(() => {
    if (pinned.current && scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(`/organize/${eventId}/messages`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        setMessages(data.messages);
      } catch {
        // Venue wifi. The next tick tries again; the thread stays on screen.
      }
    }

    const timer = setInterval(poll, POLL_MS);
    poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [eventId]);

  function send(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;

    setSending(true);
    router.post(
      `/organize/${eventId}/messages`,
      { body, urgent },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setBody("");
          setUrgent(false);
        },
        onFinish: () => setSending(false),
      },
    );
  }

  return (
    <Panel
      title="hq-thread.txt"
      actions={isHq ? <Badge tone="neutral">You are HQ</Badge> : undefined}
    >
      <div className="flex flex-col gap-4">
        <p className="text-caption text-ink-muted">
          {isHq
            ? "Anything you send here reaches the organizer and their team."
            : "Reaches HQ directly. Tick urgent if something's going wrong today - it emails us straight away."}
        </p>

        <div
          ref={scroller}
          onScroll={onScroll}
          className="border-line/20 flex max-h-96 flex-col gap-3 overflow-y-auto border-y py-3"
        >
          {messages.length === 0 ? (
            <p className="text-body text-ink-soft py-4 text-center">
              Nothing here yet. Say hello, or shout if something's wrong.
            </p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={clsx(
                  "flex gap-2",
                  m.from_hq ? "flex-row" : "flex-row-reverse",
                )}
              >
                <Avatar
                  src={m.avatar}
                  name={m.author_name}
                  seed={m.author_id}
                  size="sm"
                />

                <div
                  className={clsx(
                    "flex max-w-[80%] min-w-0 flex-col gap-0.5",
                    m.from_hq ? "items-start" : "items-end",
                  )}
                >
                  <div className="text-caption text-ink-soft flex items-center gap-2">
                    <span className="font-bold">{m.author_name}</span>
                    <span>{timeLabel(m.created_at)}</span>
                    {m.urgent && <Badge tone="danger">urgent</Badge>}
                  </div>

                  <div
                    className={clsx(
                      "border-line text-body border-2 px-3 py-2 whitespace-pre-wrap",
                      m.from_hq
                        ? "bg-surface text-ink"
                        : m.urgent
                          ? "bg-sunset-orange/20 text-ink"
                          : "bg-goldenrod/40 text-ink",
                    )}
                  >
                    {m.body}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={send} className="flex flex-col gap-2">
          <Textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={isHq ? "Reply to the organizer…" : "Message HQ…"}
          />

          <div className="flex flex-wrap items-center gap-3">
            {!isHq && (
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={urgent}
                  onChange={(e) => setUrgent(e.target.checked)}
                />
                <span className="text-caption text-ink font-bold">
                  Urgent - email HQ now
                </span>
              </label>
            )}

            <Button
              type="submit"
              size="sm"
              disabled={sending || !body.trim()}
              className="ml-auto"
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
}
